import Box from "@mui/material/Box"
import Tooltip from "@mui/material/Tooltip"
import { useFormik } from "formik"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { array, mixed, number, object, ObjectSchema, string } from "yup"

import { faTrashXmark } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import MuiAutoComplete from "components/MuiAutocomplete"
import { Option } from "components/MuiAutocomplete/shared"
import { MuiTableColumn } from "components/MuiTable"
import MuiTextField from "components/MuiTextField"
import { ProjectFormPageProps } from "components/ProjectForm"
import { EProjectFormPages } from "components/ProjectForm/config"
import { setChargersAndMaterialsForm, setProjectId } from "components/ProjectForm/ProjectFormSlice"
import { DEAL_TYPE } from "components/RaiseTicketForm/config/projectFormConstants"
import EModuleIds from "enums/ModuleEnum"
import { enqueueSnackbar } from "notistack"
import { useGetBrandsQuery, useGetProductsQuery } from "pages/Common/commonService"
import { useRaiseLogisticsTicketMutation } from "pages/Logistics/LogisticsServices"
import {
    IMaterialDetailsPageData,
    useLazyGetProjectFormDetailsByStepQuery,
    useParseChargersFileMutation,
    useParseMaterialsFileMutation,
    useUpdateTicketPageDataMutation,
} from "pages/Projects/ProjectServices"
import { ParsedChargerRow } from "pages/Projects/ProjectTypes"
import { useGetReportTemplatesQuery } from "pages/Settings/SettingsService"
import { useDispatch, useSelector } from "react-redux"
import { TStore } from "store/Store"
import CloneChargers from "./CloneChargers"

export enum EChargersAndMaterialsFields {
    CHARGER_FILE = "charger_file",
    MATERIAL_FILE = "material_file",
    CHARGERS = "chargers",
    MATERIALS = "materials",
}

export type ChargerRow = {
    sku_code: string
    display_name: string
    power_rating: number
    branding: number
    product_id?: number
    plp_id?: number
}

export type MaterialRow = {
    sku_code: string
    description: string
    quantity: number
    product_id?: number
    remarks: string
}

export type ChargersAndMaterialsFormValues = {
    [EChargersAndMaterialsFields.CHARGER_FILE]?: FileList | null
    [EChargersAndMaterialsFields.MATERIAL_FILE]?: FileList | null
    [EChargersAndMaterialsFields.CHARGERS]: ChargerRow[]
    [EChargersAndMaterialsFields.MATERIALS]: MaterialRow[]
}

const initialValues: ChargersAndMaterialsFormValues = {
    [EChargersAndMaterialsFields.CHARGER_FILE]: null,
    [EChargersAndMaterialsFields.MATERIAL_FILE]: null,
    [EChargersAndMaterialsFields.CHARGERS]: [],
    [EChargersAndMaterialsFields.MATERIALS]: [],
}

const schema: ObjectSchema<ChargersAndMaterialsFormValues> = object({
    [EChargersAndMaterialsFields.CHARGER_FILE]: mixed<any>().nullable(),
    [EChargersAndMaterialsFields.MATERIAL_FILE]: mixed<any>().nullable(),
    [EChargersAndMaterialsFields.CHARGERS]: array()
        .of(
            object({
                sku_code: string().required(),
                display_name: string().required(),
                power_rating: number().required(),
                branding: number().required(),
            })
        )
        .required(),
    [EChargersAndMaterialsFields.MATERIALS]: array()
        .of(
            object({
                sku_code: string().required(),
                description: string().required(),
                quantity: number().required(),
                remarks: string().default(""),
            })
        )
        .required(),
})

const CHARGER_ROW_DATA = {
    sku_code: "",
    display_name: "",
    power_rating: 0,
    branding: 0,
    product_id: 0,
}

const MATERIAL_ROW_DATA = {
    sku_code: "",
    description: "",
    quantity: 0,
    remarks: "",
    product_id: 0,
}

/**
 * The charger sheet carries a qty per row; the table is one row per physical charger, so a
 * parsed row with qty N expands into N identical rows. Branding is not a sheet column, so it
 * always comes back null and stays for the user to pick per row.
 */
export const expandParsedChargerRows = (rows: ParsedChargerRow[]): ChargerRow[] =>
    rows.flatMap(row => {
        const quantity = Math.max(1, Math.floor(row.quantity) || 1)
        return Array.from({ length: quantity }, () => ({
            sku_code: row.sku_code || "",
            display_name: row.description || "",
            power_rating: row.operational_power_rating || 0,
            branding: row.branding_id ?? 0,
            product_id: row.product_id,
        }))
    })

/**
 * Resolves the SKU cell's selected option. A parsed row carries both `product_id` and
 * `sku_code`, so match on `product_id` first — it is the stable key — then on the code. The
 * last fallback is a synthetic option: the SKU dropdown is filtered (`is_charger`, and
 * `operational_sku_filter` on outright deals) while the upload sheet's SKU list is not, so a
 * code that resolved server-side can still be missing from the options and must not render blank.
 */
export const resolveSkuOption = (options: Option[], row?: { product_id?: number; sku_code?: string }): Option => {
    const { product_id, sku_code } = row ?? {}
    return (
        (product_id ? options.find(option => option.id === product_id) : undefined) ??
        (sku_code ? options.find(option => option.title === sku_code) : undefined) ??
        (sku_code ? { id: product_id ?? "", title: sku_code } : { id: "", title: "" })
    )
}

/** Summarises a parse response as a single snackbar, including the all-rows-rejected case. */
export const announceParseResult = (
    entity: "charger" | "material",
    response: { rows?: unknown[]; valid_rows: number; invalid_rows: number; errors?: { error: string }[] },
    rowsAdded: number
) => {
    if (!response?.rows?.length) {
        const reason = response?.errors?.[0]?.error
        enqueueSnackbar(`No valid ${entity} rows found in the file.${reason ? ` ${reason}` : ""}`, {
            variant: "error",
        })
        return
    }
    if (response.invalid_rows > 0) {
        enqueueSnackbar(
            `Parsed ${response.valid_rows} valid rows. ${response.invalid_rows} rows had errors.${
                rowsAdded !== response.valid_rows ? ` Added ${rowsAdded} ${entity}s.` : ""
            }`,
            { variant: "warning" }
        )
        return
    }
    enqueueSnackbar(
        rowsAdded === response.valid_rows
            ? `Successfully parsed ${rowsAdded} ${entity} rows`
            : `Added ${rowsAdded} ${entity}s from ${response.valid_rows} valid rows`,
        { variant: "success" }
    )
}

const useChargersAndMaterials = (props: ProjectFormPageProps) => {
    const dispatch = useDispatch()
    const [showMoreChargers, setShowMoreChargers] = useState(false)
    const [showMoreMaterials, setShowMoreMaterials] = useState(false)
    const hasPrefilled = useRef(false)
    const {
        project_id,
        chargers_and_materials_form,
        deal_details_form,
        logistics_basic_details_form,
        account_n_sites_form,
    } = useSelector((state: TStore) => state.ProjectForm)
    const isOutright = deal_details_form?.deal_type?.id === DEAL_TYPE.OUTRIGHT
    const [getProjectFormDetailsByStep] = useLazyGetProjectFormDetailsByStepQuery()
    const [raiseLogisticsTicket, { isLoading: isRaisingLogisticsTicket }] = useRaiseLogisticsTicketMutation()

    const { data: chargerProducts } = useGetProductsQuery({
        is_charger: 1,
        ...(isOutright && { operational_sku_filter: 0 as const }),
    })
    const { data: materialProducts } = useGetProductsQuery({
        is_material: 1,
        ...(isOutright && { operational_sku_filter: 0 as const }),
    })
    const { data: brands } = useGetBrandsQuery()
    const [parseMaterialsFile, { isLoading: isParsingMaterials }] = useParseMaterialsFileMutation()
    const [parseChargersFile, { isLoading: isParsingChargers }] = useParseChargersFileMutation()
    const [updateTicketPageData, { isLoading: isUpdatingPageData }] = useUpdateTicketPageDataMutation()

    const { data: materialTemplate, isFetching: isFetchingMaterialTemplate } = useGetReportTemplatesQuery({
        isCharger: false,
    })
    const { data: chargerTemplate, isFetching: isFetchingChargerTemplate } = useGetReportTemplatesQuery({
        isCharger: true,
    })
    const isLogisticsModule = props.moduleId === EModuleIds.LOGISTICS

    // Whether the current page is the final step of the form. In Solo logistics the ticket is
    // raised on the last page — this may be Payment Details (handled in usePaymentDetails) or
    // Chargers & Materials, so the raise must be able to happen from either page.
    const isLastPage = !!props.pages?.length && props.pages[props.pages.length - 1] === props.pageData?.title

    function buildLogisticsPayload(chargers: ChargerRow[], materials: MaterialRow[]) {
        const formValues = {
            ...logistics_basic_details_form,
            ...deal_details_form,
            ...account_n_sites_form,
        }
        return {
            template_id: formValues.template_id?.id,
            project_name: formValues.project_name,
            source_site_id: formValues.source_site_id,
            destination_site_id: formValues.destination_site_id,
            accounts: formValues.account_roles,
            purchase_order: formValues.purchase_order?.id,
            category_id: formValues.category_id?.id,
            visit_by_date: formValues.visit_by_date,
            visit_by_time: formValues.visit_by_time?.id,
            total_amount: "",
            contains_rfid: false,
            // Only ever set for a complaint-linked category; null for every other one.
            // When the source is COMPLAINT_DRAWER the backend re-derives both from the drawer
            // context and ignores what is sent here.
            complaint_id: formValues.complaint_id?.id ?? null,
            complaint_link_source: formValues.complaint_link_source ?? null,
            source_poc: {
                name: formValues.source_poc?.on_ground_poc_name,
                phone: formValues.source_poc?.on_ground_poc_phone,
                email: formValues.source_poc?.on_ground_poc_email,
            },
            destination_poc: {
                name: formValues.destination_poc?.on_ground_poc_name,
                phone: formValues.destination_poc?.on_ground_poc_phone,
                email: formValues.destination_poc?.on_ground_poc_email,
            },
            chargers: chargers.map(charger => ({
                product_id: charger.product_id,
                branding_id: charger.branding,
                operational_power_rating: charger.power_rating,
                remarks: charger.display_name,
            })),
            materials: materials.map(material => ({
                product_id: material.product_id,
                quantity: material.quantity,
                remarks: material.description,
            })),
        }
    }

    async function handleLogisticsSubmit(chargers: ChargerRow[], materials: MaterialRow[]) {
        dispatch(setChargersAndMaterialsForm({ chargers, materials }))

        // Not the last page: Payment Details follows and will raise the ticket, so just advance.
        if (!isLastPage) {
            props.handleNextStep()
            return
        }

        try {
            const logResult = await raiseLogisticsTicket(buildLogisticsPayload(chargers, materials)).unwrap()
            dispatch(setProjectId(logResult.project_id))
            props.handleNextStep(logResult.project_id)
        } catch (error) {
            console.error("Error raising logistics ticket:", error)
        }
    }

    const handleSubmit = async (values: ChargersAndMaterialsFormValues) => {
        const chargers = values[EChargersAndMaterialsFields.CHARGERS]
        const materials = values[EChargersAndMaterialsFields.MATERIALS]

        if (chargers.length === 0 && materials.length === 0) {
            enqueueSnackbar("At least chargers or materials must be provided", { variant: "error" })
            return
        }

        if (isLogisticsModule) {
            await handleLogisticsSubmit(chargers, materials)
            return
        }

        if (!project_id) {
            enqueueSnackbar("Project ID is required", { variant: "error" })
            return
        }

        const payload: IMaterialDetailsPageData = {
            page_title: props.pageData.title as EProjectFormPages.MATERIAL_DETAILS,
        }

        if (chargers.length > 0) {
            payload.chargers = {
                project_id,
                rows: chargers.map(charger => ({
                    ...(charger.plp_id && { plp_id: charger.plp_id }),
                    product_id: charger.product_id ?? 0,
                    branding_id: charger.branding,
                    operational_power_rating: String(charger.power_rating),
                })),
            }
        }

        if (materials.length > 0) {
            payload.materials = {
                project_id,
                rows: materials.map(material => ({
                    product_id: material.product_id || 0,
                    quantity: material.quantity,
                    remarks: material.remarks,
                })),
            }
        }

        try {
            await updateTicketPageData(payload).unwrap()
            props.handleNextStep()
            enqueueSnackbar("Material details saved successfully", { variant: "success" })
        } catch (error) {
            console.error("Error updating ticket page data:", error)
            enqueueSnackbar("Error saving material details", { variant: "error" })
        }
    }

    const formik = useFormik<ChargersAndMaterialsFormValues>({
        initialValues: chargers_and_materials_form
            ? {
                  ...initialValues,
                  chargers: chargers_and_materials_form.chargers,
                  materials: chargers_and_materials_form.materials,
              }
            : initialValues,
        validationSchema: schema,
        validateOnChange: false,
        onSubmit: handleSubmit,
        enableReinitialize: true,
    })

    const fetchAndPrefillChargersAndMaterialsData = useCallback(async () => {
        if (!props.isEditMode || !props.projectId || hasPrefilled.current) return
        if (isLogisticsModule) return

        // Claimed before the request, not after: this callback is re-created on every render
        // (it closes over `formik` and the async product lists), so a flag set after the await
        // lets several prefills run at once and a late one overwrites freshly parsed file rows.
        hasPrefilled.current = true

        try {
            const response = await getProjectFormDetailsByStep({
                project_id: props.projectId,
                page_title: EProjectFormPages.MATERIAL_DETAILS,
            }).unwrap()

            const pageFormData = response?.pages?.[EProjectFormPages.MATERIAL_DETAILS]
            if (!pageFormData) return

            const data = pageFormData.page_data || pageFormData
            let chargerRows: ChargerRow[] = []
            let materialRows: MaterialRow[] = []

            if (data.chargers?.rows?.length > 0) {
                chargerRows = data.chargers.rows.map((c: any) => ({
                    sku_code: c.sku_code || "",
                    plp_id: c.project_product_id || 0,
                    display_name: c.display_name || "",
                    power_rating: c.ops_power_rating || 0,
                    branding: c.branding_id || 0,
                    product_id: chargerProducts?.find(p => p.sku_code === c.sku_code)?.id || 0,
                }))
                formik.setFieldValue(EChargersAndMaterialsFields.CHARGERS, chargerRows)
                setShowMoreChargers(true)
            }

            if (data.materials?.rows?.length > 0) {
                materialRows = data.materials.rows.map((m: any) => ({
                    sku_code: m.sku_code || "",
                    description: m.display_name || "",
                    quantity: m.quantity || 0,
                    remarks: m.remarks || "",
                    product_id: materialProducts?.find(p => p.sku_code === m.sku_code)?.id || 0,
                }))

                formik.setFieldValue(EChargersAndMaterialsFields.MATERIALS, materialRows)
                setShowMoreMaterials(true)
            }

            dispatch(
                setChargersAndMaterialsForm({
                    chargers: chargerRows,
                    materials: materialRows,
                })
            )
        } catch (error) {
            console.error("Error fetching chargers/materials prefill data:", error)
        }
    }, [
        props.isEditMode,
        props.projectId,
        isLogisticsModule,
        getProjectFormDetailsByStep,
        formik,
        chargerProducts,
        materialProducts,
        dispatch,
    ])

    useEffect(() => {
        fetchAndPrefillChargersAndMaterialsData()
    }, [fetchAndPrefillChargersAndMaterialsData])

    const chargerSkuOptions = useMemo(() => {
        return (
            chargerProducts?.map(product => ({
                id: product.id,
                title: product.sku_code,
            })) || []
        )
    }, [chargerProducts])

    const materialSkuOptions = useMemo(() => {
        return (
            materialProducts?.map(product => ({
                id: product.id,
                title: product.sku_code,
            })) || []
        )
    }, [materialProducts])

    const brandingOptions = useMemo(() => {
        return (
            brands?.map(brand => ({
                id: brand.id,
                title: brand.name,
            })) || []
        )
    }, [brands])

    const updateChargerRow = (rowIndex: number, key: keyof ChargerRow, value: any) => {
        const updated = [...formik.values[EChargersAndMaterialsFields.CHARGERS]]
        if (key === "sku_code") {
            const sku = chargerSkuOptions.find(option => option.id === value)
            if (sku) {
                updated[rowIndex] = {
                    ...updated[rowIndex],
                    [key]: sku.title,
                    product_id: value,
                    plp_id: undefined,
                    display_name: chargerProducts?.find(product => product.id === value)?.display_name || "",
                    power_rating: chargerProducts?.find(product => product.id === value)?.charger_power_rating || 0,
                }
            } else {
                updated[rowIndex] = {
                    ...updated[rowIndex],
                    product_id: value,
                    plp_id: undefined,
                    [key]: "",
                    display_name: "",
                }
            }
        } else {
            updated[rowIndex] = { ...updated[rowIndex], [key]: value }
        }
        formik.setFieldValue(EChargersAndMaterialsFields.CHARGERS, updated)
    }

    const updateMaterialRow = (rowIndex: number, key: keyof MaterialRow, value: any) => {
        const updated = [...formik.values[EChargersAndMaterialsFields.MATERIALS]]
        if (key === "sku_code") {
            const sku = materialSkuOptions.find(option => option.id === value)
            if (sku) {
                updated[rowIndex] = {
                    ...updated[rowIndex],
                    [key]: sku.title,
                    product_id: value,
                    description: materialProducts?.find(product => product.id === value)?.display_name || "",
                }
            } else {
                updated[rowIndex] = {
                    ...updated[rowIndex],
                    [key]: "",
                    description: "",
                }
            }
        } else {
            updated[rowIndex] = { ...updated[rowIndex], [key]: value }
        }
        formik.setFieldValue(EChargersAndMaterialsFields.MATERIALS, updated)
    }

    const handleDeleteChargerRow = (rowIndex: number) => {
        const updated = [...formik.values[EChargersAndMaterialsFields.CHARGERS]]
        updated.splice(rowIndex, 1)
        formik.setFieldValue(EChargersAndMaterialsFields.CHARGERS, updated)
    }

    const handleDeleteMaterialRow = (rowIndex: number) => {
        const updated = [...formik.values[EChargersAndMaterialsFields.MATERIALS]]
        updated.splice(rowIndex, 1)
        formik.setFieldValue(EChargersAndMaterialsFields.MATERIALS, updated)
    }

    const handleCloneChargerRow = (count: number, chargerRow: ChargerRow) => {
        const updated = [...formik.values[EChargersAndMaterialsFields.CHARGERS]]
        const { plp_id, ...clonedRow } = chargerRow
        for (let i = 0; i < count; i++) {
            updated.push(clonedRow as ChargerRow)
        }
        formik.setFieldValue(EChargersAndMaterialsFields.CHARGERS, updated)
    }

    const handleDownloadTemplate = (type: "charger" | "material") => {
        const data = type === "charger" ? chargerTemplate : materialTemplate
        const link = data?.link.urls.download
        if (!link) return
        const downloadLink = document.createElement("a")
        downloadLink.href = link
        downloadLink.download = type === "charger" ? "charger_template.xlsx" : "material_template.xlsx"
        downloadLink.click()
    }

    const chargerColumns: MuiTableColumn<any>[] = [
        { id: "index", label: "S.No." },
        {
            id: "sku_code",
            label: "SKU Code",
            width: 150,
            render: (_, row, rowIndex) => {
                return (
                    <MuiAutoComplete
                        disablePortal={false}
                        size="small"
                        containerHeight="100%"
                        options={chargerSkuOptions}
                        value={resolveSkuOption(chargerSkuOptions, row)}
                        onChange={(e: any) => updateChargerRow(rowIndex, "sku_code", e?.id || "")}
                    />
                )
            },
        },
        {
            id: "display_name",
            label: "Display Name",
            width: 400,
            render: value => {
                return (
                    <Tooltip title={value}>
                        <Box
                            maxWidth={"300px"}
                            textOverflow={"ellipsis"}
                            overflow={"hidden"}
                            whiteSpace={"nowrap"}
                            padding={"4px 8px"}>
                            {value || "-"}
                        </Box>
                    </Tooltip>
                )
            },
        },
        {
            id: "power_rating",
            label: "Ops Power Rating (KWh)*",
            width: 200,
            render: (value, _, rowIndex) => {
                const hasError = formik.submitCount > 0 && (!value || value === 0)
                return (
                    <MuiTextField
                        value={value}
                        onChange={e => updateChargerRow(rowIndex, "power_rating", e.target.value)}
                        type="number"
                        size="small"
                        error={hasError}
                        sx={{
                            height: "100%",
                            "& .MuiInputBase-root": { borderRadius: 0 },
                            "& .MuiInputBase-input": { padding: "4px 8px" },
                            "& .MuiOutlinedInput-root": hasError
                                ? {
                                      "& fieldset": { borderColor: "#F94054" },
                                      "&:hover fieldset": { borderColor: "#F94054" },
                                  }
                                : {},
                        }}
                    />
                )
            },
        },
        {
            id: "branding",
            label: "Branding",
            width: 200,
            render: (value, _, rowIndex) => {
                return (
                    <MuiAutoComplete
                        disablePortal={false}
                        disableClearable
                        size="small"
                        containerHeight="100%"
                        options={brandingOptions}
                        value={brandingOptions.find(brand => brand.id === value) || { id: "", title: "" }}
                        onChange={(e: any) => updateChargerRow(rowIndex, "branding", e?.id || "")}
                    />
                )
            },
        },
        {
            id: "custom",
            label: "",
            width: 100,
            render: (_, row, rowIndex) => {
                return (
                    <Box display={"flex"} gap={1} alignItems={"center"} padding={"4px 8px"}>
                        <Box
                            width={"20px"}
                            height={"20px"}
                            display={"flex"}
                            justifyContent={"center"}
                            borderRadius={"50%"}
                            bgcolor={"#F940541A"}
                            alignItems={"center"}
                            sx={{
                                cursor: "pointer",
                            }}
                            onClick={() => handleDeleteChargerRow(rowIndex)}>
                            <FontAwesomeIcon icon={faTrashXmark} color="#F94054" size="sm" />
                        </Box>
                        <CloneChargers onClone={count => handleCloneChargerRow(count, row)} />
                    </Box>
                )
            },
        },
    ]

    const materialColumns: MuiTableColumn<any>[] = [
        { id: "index", label: "S.No." },
        {
            id: "sku_code",
            label: "SKU Code",
            width: 150,
            render: (_, row, rowIndex) => {
                return (
                    <MuiAutoComplete
                        disablePortal={false}
                        size="small"
                        containerHeight="100%"
                        options={materialSkuOptions}
                        value={resolveSkuOption(materialSkuOptions, row)}
                        onChange={(e: any) => updateMaterialRow(rowIndex, "sku_code", e?.id || "")}
                    />
                )
            },
        },
        {
            id: "description",
            label: "Display Name",
            width: 400,
            render: value => {
                return (
                    <Tooltip title={value}>
                        <Box
                            width={"150px"}
                            textOverflow={"ellipsis"}
                            overflow={"hidden"}
                            whiteSpace={"nowrap"}
                            padding={"4px 8px"}>
                            {value || "-"}
                        </Box>
                    </Tooltip>
                )
            },
        },
        {
            id: "quantity",
            label: "Quantity*",
            render: (value, _, rowIndex) => {
                const hasError = formik.submitCount > 0 && (!value || value === 0)
                return (
                    <MuiTextField
                        value={value}
                        onChange={e => updateMaterialRow(rowIndex, "quantity", e.target.value)}
                        type="number"
                        size="small"
                        error={hasError}
                        sx={{
                            height: "100%",
                            "& .MuiInputBase-root": { borderRadius: 0 },
                            "& .MuiInputBase-input": { padding: "4px 8px" },
                            "& .MuiOutlinedInput-root": hasError
                                ? {
                                      "& fieldset": { borderColor: "#F94054" },
                                      "&:hover fieldset": { borderColor: "#F94054" },
                                  }
                                : {},
                        }}
                    />
                )
            },
        },
        {
            id: "remarks",
            label: "Remarks",
            width: 150,
            render: (value, _, rowIndex) => {
                return (
                    <MuiTextField
                        value={value}
                        onChange={e => updateMaterialRow(rowIndex, "remarks", e.target.value)}
                        size="small"
                        sx={{
                            height: "100%",
                            "& .MuiInputBase-root": { borderRadius: 0 },
                            "& .MuiInputBase-input": { padding: "4px 8px" },
                        }}
                    />
                )
            },
        },
        {
            id: "custom",
            label: "",
            width: 100,
            render: (_, __, rowIndex) => {
                return (
                    <Box display={"flex"} gap={1} alignItems={"center"} padding={"4px 8px"}>
                        <Box
                            width={"20px"}
                            height={"20px"}
                            display={"flex"}
                            justifyContent={"center"}
                            borderRadius={"50%"}
                            bgcolor={"#F940541A"}
                            alignItems={"center"}
                            sx={{ cursor: "pointer" }}
                            onClick={() => handleDeleteMaterialRow(rowIndex)}>
                            <FontAwesomeIcon icon={faTrashXmark} color="#F94054" size="sm" />
                        </Box>
                    </Box>
                )
            },
        },
    ]

    const handleAddChargerRow = () => {
        formik.setFieldValue(
            EChargersAndMaterialsFields.CHARGERS,
            formik.values[EChargersAndMaterialsFields.CHARGERS].concat([CHARGER_ROW_DATA])
        )
    }

    const handleAddMaterialRow = () => {
        formik.setFieldValue(
            EChargersAndMaterialsFields.MATERIALS,
            formik.values[EChargersAndMaterialsFields.MATERIALS].concat([MATERIAL_ROW_DATA])
        )
    }

    const handleShowMoreChargers = () => {
        setShowMoreChargers(!showMoreChargers)
    }

    const handleShowMoreMaterials = () => {
        setShowMoreMaterials(!showMoreMaterials)
    }

    const handleParseMaterialsFile = async (files: FileList) => {
        if (files) {
            formik.setFieldValue(EChargersAndMaterialsFields.MATERIAL_FILE, files)
            const file = files[0]
            try {
                const formData = new FormData()
                formData.append("file", file)
                formData.append("project_id", project_id?.toString() || "")
                const response = await parseMaterialsFile(formData).unwrap()
                const materialRows: MaterialRow[] = (response?.rows ?? []).map(row => ({
                    sku_code: row.sku_code || "",
                    description: row.description || "",
                    quantity: row.quantity || 0,
                    remarks: row.remarks || "",
                    product_id: row.product_id,
                }))
                if (materialRows.length > 0) {
                    formik.setFieldValue(EChargersAndMaterialsFields.MATERIALS, materialRows)
                    setShowMoreMaterials(true)
                }
                announceParseResult("material", response, materialRows.length)
            } catch (error) {
                console.error("Error parsing materials file:", error)
                enqueueSnackbar("Error parsing materials file", {
                    variant: "error",
                })
            }
        }
    }

    const handleParseChargersFile = async (files: FileList) => {
        if (files) {
            formik.setFieldValue(EChargersAndMaterialsFields.CHARGER_FILE, files)
            const file = files[0]
            try {
                const formData = new FormData()
                formData.append("file", file)
                formData.append("project_id", project_id?.toString() || "")
                const response = await parseChargersFile(formData).unwrap()
                const chargerRows = expandParsedChargerRows(response?.rows ?? [])
                if (chargerRows.length > 0) {
                    formik.setFieldValue(EChargersAndMaterialsFields.CHARGERS, chargerRows)
                    setShowMoreChargers(true)
                }
                announceParseResult("charger", response, chargerRows.length)
            } catch (error) {
                console.error("Error parsing chargers file:", error)
                enqueueSnackbar("Error parsing chargers file", {
                    variant: "error",
                })
            }
        }
    }

    return {
        states: {
            showMoreChargers,
            showMoreMaterials,
            chargerColumns,
            materialColumns,
            formik,
            isUpdatingPageData,
            isRaisingLogisticsTicket,
            isFetchingChargerTemplate,
            isFetchingMaterialTemplate,
            isLastPage,
        },
        functions: {
            setShowMoreChargers,
            setShowMoreMaterials,
            updateChargerRow,
            updateMaterialRow,
            handleShowMoreChargers,
            handleAddChargerRow,
            handleAddMaterialRow,
            handleShowMoreMaterials,
            handleParseMaterialsFile,
            handleParseChargersFile,
            handleDownloadTemplate,
        },
    }
}

export default useChargersAndMaterials
